import { useRef, useState } from "react";
import Auth from "../../Auth";
import { SudoButton } from "../../sudokit/components/SudoButton";

type Check = {
  userId: string;
  status: string;
  reason: string | null;
  detail?: string | null;
  repairable: boolean;
  interval: string | null;
  expectedInterval: string | null;
  planTitle: string | null;
  currentPeriodEnd: string | null;
  nextProvisionAt: string | null;
  cancelAtPeriodEnd: boolean | null;
  creditAction: string;
  creditAmount: number | null;
  expectedState: string;
};

const reasons: Record<string, string> = {
  "ledger-review-required": "The current credit cycle needs engineering review before repair.",
  expired: "The subscription has expired or has no paid expiration date.",
  inactive: "The subscription is not active.",
  "not-revenuecat": "This repair only applies to RevenueCat subscriptions.",
  "plan-not-found": "The product could not be identified. Engineering review is needed.",
  "not-annual": "This is a monthly product; the annual interval repair does not apply.",
};

function dateLabel(value: string | null) {
  return value ? new Date(value).toLocaleString(undefined, { timeZoneName: "short" }) : "Not available";
}

async function errorMessage(error: any) {
  try {
    const body = await error.response?.clone().json();
    if (body?.message) return body.message;
  } catch { /* Fall back to the request error. */ }
  return error.message || "Unable to check or repair annual credits.";
}

export function AnnualCreditsRepair({ userId, toolsEnabled, setToolsEnabled, onRepaired }: {
  onRepaired?: () => Promise<void>;
  userId: string;
  toolsEnabled: boolean;
  setToolsEnabled: (enabled: boolean) => void;
}) {
  const [check, setCheck] = useState<Check | null>(null);
  const [busy, setBusy] = useState<"check" | "repair" | null>(null);
  const inFlight = useRef(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const baseUrl = `/api/subscriptions/${encodeURIComponent(userId)}/annualCredits`;

  async function run(action: "check" | "repair") {
    if (inFlight.current || !toolsEnabled || (action === "repair" && (!check?.repairable || check.userId !== userId))) return;
    inFlight.current = true;
    setBusy(action);
    setToolsEnabled(false);
    setError("");
    setMessage("");
    // Any failed request requires a fresh check before another repair attempt.
    setCheck(null);
    let repaired = false;
    try {
      if (action === "repair") {
        const response = await Auth.fetchWithIdToken(`${baseUrl}/repair`, { expectedState: check!.expectedState });
        if (!response.success) throw new Error(response.message || "Repair failed.");
        repaired = true;
        setMessage(response.auditWarning || "Annual credit repair completed. Paid expiration and cancellation preferences were preserved.");
      }
      const response = await Auth.fetchWithIdToken(`${baseUrl}/check`, {});
      if (!response.success) throw new Error(response.message || "Check failed.");
      setCheck(response.check);
      if (repaired) await onRepaired?.();
    } catch (err) {
      const detail = await errorMessage(err);
      setError(repaired ? `Repair completed, but the follow-up check failed: ${detail}` : detail);
    } finally {
      inFlight.current = false;
      setBusy(null);
      setToolsEnabled(true);
    }
  }

  return (
    <div className="w-full border-t border-purple-200 pt-4">
      <h5 className="font-semibold text-purple-900 mb-2">Annual monthly credits</h5>
      <p className="text-sm text-purple-700 mb-3">Check whether this mobile annual plan is recorded correctly for automatic monthly credits.</p>
      <div className="flex flex-wrap gap-3">
        <SudoButton type="button" variant="secondary" disabled={!toolsEnabled || !!busy} onClick={() => void run("check")}>
          {busy === "check" ? "Checking…" : "Check annual credits"}
        </SudoButton>
        <SudoButton type="button" variant="secondary" disabled={!toolsEnabled || !!busy || !check?.repairable || check.userId !== userId} onClick={() => void run("repair")}>
          {busy === "repair" ? "Repairing…" : "Repair annual credits"}
        </SudoButton>
      </div>
      {!check && !busy && !message && <p className="text-sm text-purple-700 mt-2">Run a check to see whether a repair is needed.</p>}
      {message && <p role="status" className="text-sm text-green-800 mt-3">{message}</p>}
      {error && <p role="alert" className="text-sm text-red-700 mt-3">{error}</p>}
      {check && (
        <div className="mt-3 text-sm text-purple-900 space-y-2" aria-live="polite">
          <p className="font-semibold">{check.status === "healthy"
            ? "The annual interval is correct and no interval repair is pending."
            : check.repairable ? "This account needs an annual credit repair."
              : reasons[check.reason || ""] || "No automatic interval repair is available."}</p>
          {check.detail && <p>{check.detail}</p>}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt>Plan</dt><dd>{check.planTitle || "Unknown"}</dd>
            <dt>Saved interval</dt><dd>{check.interval || "Missing"}</dd>
            <dt>Expected interval</dt><dd>{check.expectedInterval || "Unknown"}</dd>
            <dt>Paid through</dt><dd>{dateLabel(check.currentPeriodEnd)}</dd>
            <dt>Next monthly credit date</dt><dd>{dateLabel(check.nextProvisionAt)}</dd>
            <dt>Cancel at period end</dt><dd>{check.cancelAtPeriodEnd ? "Yes" : "No"}</dd>
          </dl>
          {check.repairable && <>
            <p>{check.creditAction === "provision"
              ? `Repair will set the interval to yearly and provision the current cycle (${check.creditAmount?.toLocaleString() ?? "plan"} base credits; rollover and bonuses follow plan rules).`
              : check.creditAction === "reconcile"
                ? "Repair will set the interval to yearly and reconnect the existing cycle grant. No new credits will be added."
                : "Repair will set the interval to yearly. No new credits will be added."}</p>
            <p>Paid expiration and cancellation preferences will be preserved.</p>
          </>}
        </div>
      )}
    </div>
  );
}
